import { CopyText } from "@/components/CopyText";
import { ItemCard } from "@/components/ItemCard";
import { handleDrawingLinkCopy } from "@/features/drawings/utils";
import { useModals } from "@/features/modals/hooks";
import { useActiveProject } from "@/features/project/ActiveProject";
import { setActiveProject } from "@/features/project/setActiveProject";
import { currencyFormatter } from "@/utils/currencyFormatter";
import {
  type NormalizedDrawing,
  type Opportunity,
  isDexfPlannerId,
  isKitchenPlannerIhp2Spec,
  isKitchenPlannerIhp3Spec,
  isOfficePlannerIdIhp2Spec,
  refreshDrawing,
} from "@repo/api";
import {
  Text,
  copyIcon,
  crossIcon,
  pencilIcon,
  shoppingBagAddIcon,
  visibilityShowIcon,
} from "@repo/skapa";
import { getLanguageLocal, t } from "@repo/translation";
import { FlexDiv, useSettings } from "@repo/ui";
import { useToast } from "@repo/ui";
import {
  type Toast,
  getDebugMessage,
  hasItems,
  isDefined,
  trackAnalytics,
} from "@repo/utils";

import { DrawingCardDetails } from "./DrawingCardDetails";

const redirectToVPCDrawing = (
  market: string,
  language: string,
  url: string,
  opportunity?: Opportunity
) => {
  if (opportunity) {
    setActiveProject(opportunity);
  }
  const link = `${import.meta.env.VITE__WEB_SALJA_V2}/${market}/${language}/v1/detail/${url}`;

  window.open(link, "_blank");
};

const redirectToGPCDrawing = async ({
  opportunity,
  drawingId,
  market,
  language,
  updateToast,
}: {
  opportunity: Opportunity;
  drawingId: string;
  market: string;
  language: string;
  updateToast: (toast: Toast) => void;
  enableOneDomain?: boolean;
}) => {
  try {
    const refreshedDrawingInfo = await refreshDrawing(
      opportunity.id,
      drawingId
    );

    const vpcId = refreshedDrawingInfo.linkedDesigns?.find(
      (d) => d.source === "VPC"
    )?.id;

    if (vpcId) {
      redirectToVPCDrawing(
        market,
        language,
        `combination/planner/${vpcId}/`,
        opportunity
      );
    } else {
      updateToast({
        id: drawingId,
        type: "error",
        title: t("prompt.failed_to_add_drawing_to_cart"),
        message: t("prompt.gpc_drawing_not_linked_to_vpc"),
      });
    }
  } catch (error) {
    updateToast({
      id: drawingId,
      type: "error",
      title: t("prompt.failed_to_add_drawing_to_cart"),
      message: getDebugMessage(error),
    });
  }
};

export const DrawingCard = ({
  editable,
  drawingInfo,
  onDisassociate,
  vpcDrawing,
  isVpDrawingValidating,
}: {
  editable: boolean;
  drawingInfo: NormalizedDrawing;
  onDisassociate?: (drawingId: string) => void;
  vpcDrawing?: NormalizedDrawing;
  isVpDrawingValidating: boolean;
}) => {
  const { pushModal } = useModals();
  const { market, language, general } = useSettings();
  const { opportunity } = useActiveProject();
  const { updateToast } = useToast();
  const onManageDrawing = () => {
    pushModal({
      type: "PLANNER",
      drawing: drawingInfo,
      onDisassociate: () => onDisassociate?.(drawingInfo.id),
    });
  };

  const onConfirmDisassociate = () => {
    if (onDisassociate) {
      pushModal({
        type: "CONFIRM",
        onConfirm: () => onDisassociate(drawingInfo.id),
        title: t("action.remove_from_project"),
        message: t("prompt.confirm_remove_drawing"),
      });
    }
  };

  const addVpcDrawingToCartToBag = {
    onClick: (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      if (drawingInfo.id) {
        redirectToVPCDrawing(
          market,
          language,
          `combination/planner/${drawingInfo.id}/`,
          opportunity
        );
      } else {
        updateToast({
          id: drawingInfo.id,
          type: "error",
          title: t("prompt.failed_to_add_drawing_to_cart"),
        });
      }
    },
    disabled: !hasItems(vpcDrawing?.items),
    loading: isVpDrawingValidating,
  };

  const addPlannerDrawingToCart = {
    onClick: (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      if (
        drawingInfo.isDexfDrawing &&
        isDexfPlannerId(drawingInfo.id) &&
        drawingInfo.applicationId !== "nkp"
      ) {
        const appType =
          drawingInfo.applicationId ?? drawingInfo.type ?? "UNKNOWN";
        redirectToVPCDrawing(
          market,
          language,
          `combination/planner/${drawingInfo.id}/?source=dexf&appType=${appType}`,
          opportunity
        );
      } else if (isKitchenPlannerIhp3Spec(drawingInfo.id)) {
        redirectToVPCDrawing(
          market,
          language,
          `kitchenplanner/KITCHEN/${drawingInfo.id}/IHP3/`,
          opportunity
        );
      } else if (isKitchenPlannerIhp2Spec(drawingInfo.id)) {
        redirectToVPCDrawing(
          market,
          language,
          `kitchenplanner/KITCHEN/${drawingInfo.id}/IHP2/`,
          opportunity
        );
      } else if (isOfficePlannerIdIhp2Spec(drawingInfo.id)) {
        redirectToVPCDrawing(
          market,
          language,
          `kitchenplanner/OFFICE/${drawingInfo.id}/IHP2`,
          opportunity
        );
      } else {
        updateToast({
          id: drawingInfo.id,
          type: "error",
          title: t("prompt.failed_to_add_drawing_to_cart"),
          message: t("prompt.drawing_type_not_supported"),
        });
      }
    },
    loading: false,
  };

  const addKreativDrawingToCart = {
    onClick: async (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      const itemsToAdd =
        drawingInfo.items?.map((item) => ({
          itemId: item.itemNo,
          quantity: item.quantity ?? 1,
        })) || [];

      if (hasItems(itemsToAdd)) {
        try {
          await window?.xdk?.cart?.addMany(itemsToAdd);
          const link = `${import.meta.env.VITE__WEB_SALJA_V2}/${market}/${language}/v1/cart/`;
          window.open(link, "_blank");
        } catch {
          updateToast({
            id: drawingInfo.id,
            type: "error",
            title: t("prompt.failed_to_add_drawing_to_cart"),
            message: t("prompt.unable_to_add_items"),
          });
        }
      } else {
        updateToast({
          id: drawingInfo.id,
          type: "error",
          title: t("prompt.failed_to_add_drawing_to_cart"),
          message: t("prompt.drawing_type_not_supported"),
        });
      }
    },
    loading: false,
  };

  const addGPCPlannerDrawingToCart = {
    onClick: async (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      if (opportunity?.id !== undefined) {
        await redirectToGPCDrawing({
          opportunity,
          drawingId: drawingInfo.id,
          market,
          language,
          updateToast,
        });
      } else {
        updateToast({
          id: drawingInfo.id,
          type: "error",
          title: t("prompt.failed_to_add_drawing_to_cart"),
        });
      }
    },
    loading: false,
  };

  const getAddToBagAction = () => {
    if (drawingInfo.type === "KREATIV") {
      return addKreativDrawingToCart;
    }
    if (drawingInfo.type === "VPC" || drawingInfo.isVPCDrawing) {
      return addVpcDrawingToCartToBag;
    }
    if (drawingInfo.type === "GPC") {
      return addGPCPlannerDrawingToCart;
    }
    return addPlannerDrawingToCart;
  };

  const menuItems = [
    drawingInfo.editUrl
      ? {
          icon: pencilIcon,
          title: t("action.edit"),
          onClick: () => {
            window.open(drawingInfo.editUrl, "_blank");
            trackAnalytics("open_drawing_edit_link");
          },
        }
      : undefined,
    drawingInfo.viewUrl
      ? {
          icon: visibilityShowIcon,
          title: t("action.view"),
          onClick: () => {
            window.open(drawingInfo.viewUrl, "_blank");
            trackAnalytics("open_drawing_view_link");
          },
        }
      : undefined,
    drawingInfo.viewUrl
      ? {
          icon: copyIcon,
          title: t("action.copy_link"),
          onClick: () => {
            handleDrawingLinkCopy(drawingInfo.viewUrl!, updateToast);
            trackAnalytics("copy_drawing_link");
          },
        }
      : undefined,
    {
      icon: crossIcon,
      title: t("action.remove_from_project"),
      onClick: () => {
        onConfirmDisassociate();
        trackAnalytics("remove_drawing_from_project");
      },
    },
  ].filter(isDefined);

  return (
    <ItemCard
      editable={editable}
      onClick={onManageDrawing}
      image={drawingInfo.image}
      amount={currencyFormatter({
        value: Number(drawingInfo.value ?? 0),
        currency: drawingInfo.currency ?? opportunity?.currency,
        locale: getLanguageLocal(language, market),
        showSign: true,
        disableDecimals: Boolean(general?.localization?.disable_decimals),
        fractions: 2,
      })}
      addedToProjectValue={drawingInfo.addedToProjectValue}
      menuItems={menuItems}
      actions={[
        {
          id: `add-to-cart-button__drawing-${drawingInfo.id}`,
          text: `${t("action.add_to_cart")} - ${drawingInfo.name}`,
          title: t("action.add_to_cart"),
          type: "emphasised",
          iconOnly: true,
          icon: shoppingBagAddIcon,
          loading: getAddToBagAction().loading,
          onClick: getAddToBagAction().onClick,
        },
      ]}
      details={<DrawingCardDetails drawing={drawingInfo} />}
      addOn={
        !drawingInfo.isVPCDrawing ? (
          <FlexDiv flexDirection="column" gap="4px">
            <CopyText
              text={drawingInfo.id}
              buttonTitle={t("action.copy_drawing_id")}
              successMessage={t("prompt.drawing_id_has_been_copied")}
            />
            {drawingInfo.shortcode && (
              <FlexDiv gap="8px" alignItems="center">
                <Text textStyle="Body.S">{`${t("label.shortcode")}:`}</Text>
                <CopyText
                  text={drawingInfo.shortcode}
                  buttonTitle={t("action.copy_shortcode")}
                  successMessage={t("prompt.shortcode_has_been_copied")}
                />
              </FlexDiv>
            )}
          </FlexDiv>
        ) : undefined
      }
    />
  );
};
